import { useEffect, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { homeShowsAgentsItself, useHomeVariant } from '../../../../lib/home-variant';
import { useBasePath } from '../../../../lib/use-base-path';
import { useWins } from '../../../../lib/use-wins';
import { winsNavRowVisible } from '../../../../lib/wins-nav';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useOnboardingEntry } from '../../../../lib/use-onboarding-entry';
import { useTranslation } from '../../../../lib/i18n';
import { useNeedsPhoneConnect, useOpenPhoneConnect } from '../../../../lib/phone-connect';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { VERIFY_CONNECT } from '../../PhoneVerifyModal';

// The rail carries destinations you can jump to from any page: Chats,
// Home (the `/` homepage grid), and Your agents.
// Everything else lives under those or under a workspace (the Symphony IA,
// matching the native drawer).
//
// Connectors used to be a destination here and is now a Settings row: "what am I
// hooked up to?" is something you check occasionally, not a place you work
// (docs/connectors-page-design.md §1). Its routes all still resolve.
// 'contacts' is deliberately NOT here: the destination moved onto the Your Agents hub, beside
// Tasks / Routines / Files / Apps, because it answers the same question those do. The route still
// exists and is still deep-linkable — only the rail entry is gone.
export type NavId = 'chats' | 'feed' | 'agents' | 'wins' | 'connect-whatsapp';

export interface SidebarNavItem {
  id: NavId;
  label: string;
  /** The Next page file, for `<Link href>`. */
  route: string;
  /** The URL the browser shows, for `<Link as>` — mount-prefixed. */
  path: string;
  isActive: boolean;
  onClick: (e: MouseEvent) => void;
}

interface SidebarNav {
  items: SidebarNavItem[];
  /** An action beside Chats, never a second primary destination. */
  newChat: { label: string; disabled: boolean; onClick: (e: MouseEvent) => void };
}

// The NAVIGATING destinations that report a click. `connect-whatsapp` reports its own, on the branch
// that opens the dialog in place — it never reaches this.
const MENU_BI_NAV_IDS = new Set<NavId>(['chats', 'agents', 'wins']);

const DESTINATIONS: { id: NavId; labelKey: string; route: string }[] = [
  { id: 'chats', labelKey: 'chats.title', route: '/chats' },
  { id: 'feed', labelKey: 'nav.catchUp', route: '/' },
  { id: 'agents', labelKey: 'nav.yourAgents', route: '/agents' },
  { id: 'wins', labelKey: 'nav.wins', route: '/wins' },
  // Conditional — offered only while there is no number on file (see `offerConnect`). It is a
  // destination rather than a card because the number is what makes WhatsApp, SMS and every
  // away-from-the-app notification work, so it stays reachable from every page until it is done.
  { id: 'connect-whatsapp', labelKey: 'nav.connectWhatsapp', route: `/?verify=${VERIFY_CONNECT}` },
];

// Which destinations the rail offers, what they're called, where they go, and what
// clicking one does. New Chat is a separate action beside the Chats destination.
// Shared by the wide rail and the mobile drawer so both behave identically and only their markup differs; each UI
// pairs an item with its glyph from ../components/nav-icons.
export function useSidebarNav({
  current,
  onClose,
}: {
  current?: NavId;
  /** Narrow drawer: close it when a destination is picked. */
  onClose?: () => void;
} = {}): SidebarNav {
  const basePath = useBasePath();
  const router = useRouter();
  const { t } = useTranslation();
  const { openMaestroChat, setChatMode } = useActiveConversation();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const { route: onboardingRoute, settled: onboardingSettled, hasConversations } = useOnboardingEntry();
  // The rail asks for a number only when there is nothing on file to use — the same question the
  // Connectors page's WhatsApp actions ask, so it is answered in one place for all of them.
  const offerConnect = useNeedsPhoneConnect();
  const openPhoneConnect = useOpenPhoneConnect();
  // Only the AGENTS WORLD home replaces Your Agents (its ring IS the team, landed on from /agents), so
  // only there does the rail drop the redundant destination. The Overview home shows agents as dots on
  // a ring — a summary — and its reader may be on the task-list tab, so it keeps the entry. The route
  // itself stays for deeplinks either way.
  const agentsIsHome = homeShowsAgentsItself(useHomeVariant());
  // Same query the Wins page reads, so opening the page later is already cached.
  const winsRead = useWins();
  const showWins = winsNavRowVisible({ count: winsRead.wins.length });
  // The rail stays mounted across pages, so look again on each navigation while nothing is known:
  // a win recorded later, or a failed first read, would otherwise leave the row's state stuck.
  const { isLoading: winsLoading, wins: knownWins, refetch: refetchWins } = winsRead;
  useEffect(() => {
    if (!winsLoading && knownWins.length === 0) refetchWins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);
  // A brand-new account (nothing to chat in yet) is greeted by the assistant first, and that greeting IS its
  // first chat. Gated on the account having no chat at all: a recipient left at NOT_STARTED beside real chats
  // would otherwise be sent home on every press, and never get a new chat.
  const kickoff = onboardingRoute === 'kickoff' && !hasConversations;
  const onNewChat = (e: MouseEvent) => {
    e.preventDefault();
    if (!onboardingSettled) return;
    onClose?.();
    logPageClickBi({ page_name: 'menu', button_name: 'new_chat', origin: 'menu' });
    // New Chat always starts a NEW chat. It used to reopen the newest chat while onboarding was unfinished,
    // and since many accounts never leave GREETED/ENGAGED, that read as "New chat opens an existing chat".
    // The onboarding thread stays in the list, one click away.
    if (kickoff) {
      // Open fullscreen so the derived kickoff greets; a fresh `?new=1` thread would block it.
      setChatMode('fullscreen');
      void router.push('/', `${basePath || '/'}`);
    } else {
      openMaestroChat();
    }
    if (router.pathname === '/chats' && !kickoff) {
      const query = {
        ...(typeof router.query.q === 'string' ? { q: router.query.q } : {}),
        ...(typeof router.query.channel === 'string' ? { channel: router.query.channel } : {}),
        new: '1',
      };
      void router.push({ pathname: '/chats', query }, `${basePath}/chats?${new URLSearchParams(query)}`, { shallow: true, scroll: false });
    }
  };

  const onClick = (id: NavId) => (e: MouseEvent) => {
    // The verification dialog is not a place — it is a step over wherever the caller already is, so it
    // opens in place and the page behind it never unmounts. Its `route` stays a real href so a
    // cmd/middle-click still lands somewhere sensible.
    //
    // It used to navigate here, because the dialog was mounted on the home alone: answering the ask
    // meant throwing away whatever the caller was doing, and on an unfinished onboarding the home then
    // resumed the setup chat fullscreen on arrival — so a click from Connectors landed on
    // `/?verify=connect&chat=fullscreen`. The mount moved to AppFrame; this is the other half of it.
    if (id === 'connect-whatsapp') {
      e.preventDefault();
      onClose?.();
      logPageClickBi({ page_name: 'menu', button_name: id, origin: 'menu' });
      openPhoneConnect();
      return;
    }
    // A real navigation from the rail/drawer — mark the entry it lands on as drawer-opened so its
    // header shows the menu, not back (native's `openedFromDrawer`).
    markNavFromDrawer();
    leaveChat();
    onClose?.();
    if (!MENU_BI_NAV_IDS.has(id)) return;
    logPageClickBi({ page_name: 'menu', button_name: id, origin: 'menu' });
  };

  return {
    items: DESTINATIONS.filter(({ id }) => (id !== 'connect-whatsapp' || offerConnect) && (id !== 'agents' || !agentsIsHome) && (id !== 'wins' || showWins)).map(({ id, labelKey, route }) => ({
      id,
      label: t(labelKey),
      route,
      path: `${basePath}${route}`,
      isActive: id === current,
      onClick: onClick(id),
    })),
    newChat: { label: t('chats.newChat'), disabled: !onboardingSettled, onClick: onNewChat },
  };
}
